import { useId, useRef, type ReactNode } from "react";
import type { ImportRunRow } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { AboutBox } from "../design/components/AboutBox";
import { Button } from "../design/components/Button";
import { DataTable } from "../design/components/DataTable";
import { Icon, type IconName } from "../design/components/Icon";
import { LinkButton } from "../design/components/LinkButton";
import { Reason } from "../design/components/Reason";
import { SettingsLayout } from "../design/components/SettingsLayout";
import { StatusPill } from "../design/components/StatusPill";
import type { Tone } from "../design/components/tone";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useAccess, type Access } from "../session/access";
import { CloseHandoverSection } from "./CloseHandoverSection";
import { CloseImportReport } from "./CloseImportReport";
import { CloseNightlySection } from "./CloseNightlySection";
import { formatDuration, runDuration, runTitle, whenParts } from "./close-import-format";
import { closeImportRunQuery, closeImportRunsQuery } from "./close-import-queries";
import "./close-import.css";

// Impostazioni → «Importazione ed esportazione» → «Importazione guidata da Close» (piano M2 Task 21; dal 1° ottobre 2026 una
// pagina della sezione «Importazione ed esportazione», con il ritorno alla sezione; il vecchio indirizzo
// `/impostazioni/importazione-close` porta qui; mock-up `importazione-close.html` e `initCloseImport`, proposte
// 1–15 del 1° ottobre 2026, ancora da confermare): le esecuzioni dalla più recente e il rapporto di quella aperta, la più
// recente se l'indirizzo non ne dice un'altra. Una prova in corso si rilegge ogni 5 secondi con fasi e record letti; una
// fermata dice dove e che cosa è rimasto; una finita ha il rapporto in quattro parti (`CloseImportReport`). In cima «Ogni sera
// da Close» (INGR-12, 18/10/2026, `CloseNightlySection`): da lì il Super admin di Gioia365 avvia prova senza salvare e importazione e accende
// quella di ogni sera alle 21:00; il comando `close-import` resta. Una riga dice chi le avvia. La vedono chi ha `import_leads` e il
// Super admin di Gioia365, come il server; agli altri non legge niente. Sotto «Ogni sera da Close», soltanto per il Super admin di Gioia365,
// «Pronti a spegnere Close?» (M8, `CloseHandoverSection`): la verifica del passaggio prima della disdetta.

export type CloseImportSearch = { esecuzione?: string };

/** La forma di un identificativo di esecuzione: un valore diverso nell'indirizzo vale come assente. */
const RUN_ID_SHAPE = /^[A-Za-z0-9_-]{1,64}$/;

export function validateCloseImportSearch(search: Record<string, unknown>): CloseImportSearch {
  return typeof search.esecuzione === "string" && RUN_ID_SHAPE.test(search.esecuzione) ? { esecuzione: search.esecuzione } : {};
}

/** Dopo quanto un'esecuzione in corso senza battito si considera ferma: lo stesso limite della ripresa nel server. */
const SILENT_AFTER_MS = 2 * 60_000;

function canSeeCloseImport(access: Access): boolean {
  return access.isPlatform || access.can("import_leads");
}

export function CloseImportPage() {
  const t = useT();
  const back = (
    <Link to="/impostazioni/importazione-ed-esportazione" className="g-button" data-variant="ghost">
      {t("importExport.backToSection")}
    </Link>
  );
  return (
    <SettingsLayout section="importExport" title={t("importExport.close.title")} actions={back}>
      <CloseImportSection />
    </SettingsLayout>
  );
}

function CloseImportSection() {
  const t = useT();
  const access = useAccess();
  const organization = access.context.organization.name;
  return (
    <div className="g-ci">
      <AboutBox title={t("closeImport.about.title")}>
        <p>{t("closeImport.about.text")}</p>
        <h2>{t("closeImport.about.whoTitle")}</h2>
        <p>{t("closeImport.about.who", { organization })}</p>
      </AboutBox>
      {canSeeCloseImport(access) ? <CloseImportRuns /> : <Reason>{t("closeImport.denied", { organization })}</Reason>}
    </div>
  );
}

const STATE: Record<ImportRunRow["status"], { tone: Tone; icon: Extract<IconName, "check" | "clock" | "minus" | "lock" | "alert">; key: "running" | "completed" | "failed" | "aborted" }> = {
  running: { tone: "accent", icon: "clock", key: "running" },
  completed: { tone: "success", icon: "check", key: "completed" },
  failed: { tone: "danger", icon: "alert", key: "failed" },
  aborted: { tone: "warning", icon: "minus", key: "aborted" },
};

function RunState({ run }: { run: ImportRunRow }) {
  const t = useT();
  const state = STATE[run.status];
  return (
    <StatusPill tone={state.tone} icon={state.icon}>
      {t(`closeImport.runs.${state.key}`)}
    </StatusPill>
  );
}

function CloseImportRuns() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const navigate = useNavigate();
  const search = useSearch({ from: "/autenticato/guscio/azienda/impostazioni/importazione-ed-esportazione/close" });
  const runs = useQuery(closeImportRunsQuery(tab));
  const titleId = useId();
  const timeZone = access.context.settings.timeZone;
  // «In corso da …» si conta fino all'ultima lettura dell'elenco, che durante una prova in corso arriva ogni 5 secondi.
  const now = runs.dataUpdatedAt;

  const list = runs.data ?? [];
  const selected = list.find((run) => run.id === search.esecuzione) ?? list[0] ?? null;
  // Aprire un'altra esecuzione cambia soltanto l'indirizzo: la pagina non torna in cima e il fuoco resta dov'è.
  const open = (runId: string) => void navigate({ to: "/impostazioni/importazione-ed-esportazione/close", search: { esecuzione: runId }, replace: true, resetScroll: false });
  // Le date della tabella, per riportarvi il fuoco quando il pulsante premuto sparisce con il dettaglio che lo conteneva.
  const dateButtons = useRef(new Map<string, HTMLButtonElement>());
  const openFromDetail = (runId: string) => {
    open(runId);
    requestAnimationFrame(() => dateButtons.current.get(runId)?.focus());
  };

  let body: ReactNode;
  if (runs.data) {
    body =
      list.length === 0 ? (
        <p className="g-ci__muted">{t("closeImport.runs.empty")}</p>
      ) : (
        <DataTable caption={t("closeImport.runs.caption")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("closeImport.runs.when")}</th>
              <th scope="col">{t("closeImport.runs.what")}</th>
              <th scope="col">{t("closeImport.runs.state")}</th>
              <th scope="col" className="g-ci__num">
                {t("closeImport.runs.duration")}
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((run) => {
              const current = run.id === selected?.id;
              const duration = formatDuration(t, runDuration(run, now));
              return (
                <tr key={run.id} aria-current={current ? "true" : undefined}>
                  <th scope="row">
                    <LinkButton
                      ref={(node) => {
                        if (node) dateButtons.current.set(run.id, node);
                        else dateButtons.current.delete(run.id);
                      }}
                      className="g-ci__link"
                      aria-pressed={current}
                      onClick={() => open(run.id)}
                    >
                      {whenParts(timeZone, run.startedAt).full}
                    </LinkButton>
                  </th>
                  <td>
                    <strong>{t(run.mode === "dry_run" ? "closeImport.runs.dryRun" : "closeImport.runs.apply")}</strong>
                    <span className="g-table__sub">
                      {run.sourceKind === "synthetic" ? t("closeImport.runs.synthetic") : t(run.mode === "dry_run" ? "closeImport.runs.dryRunSub" : "closeImport.runs.applySub")}
                    </span>
                  </td>
                  <td>
                    <RunState run={run} />
                  </td>
                  <td className="g-ci__num">{run.status === "running" ? t("closeImport.runs.runningFor", { duration }) : duration}</td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      );
  } else if (runs.isError) {
    body = <Problem error={runs.error} onRetry={() => void runs.refetch()} />;
  } else {
    body = <p className="g-ci__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <CloseNightlySection onStarted={open} />
      {access.isPlatform ? <CloseHandoverSection /> : null}
      <p className="g-ci__auto">
        <Icon name="info" />
        <span>
          <strong>{t("closeImport.whoStartsTitle")}</strong>
          <span>{t("closeImport.whoStartsText")}</span>
        </span>
      </p>
      <section className="g-ci__set" aria-labelledby={titleId}>
        <div className="g-ci__set-title">
          <h2 id={titleId}>{t("closeImport.runs.title")}</h2>
          <p className="g-ci__muted">{t("closeImport.runs.intro")}</p>
        </div>
        {body}
      </section>
      {selected ? <RunDetail key={selected.id} row={selected} runs={list} onOpen={openFromDetail} /> : null}
    </>
  );
}

function Problem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  return (
    <div className="g-ci__problem">
      <Reason tone="warning" role="alert">
        {messageForError(t, error)}
      </Reason>
      <Button onClick={onRetry}>{t("common.retry")}</Button>
    </div>
  );
}

function RunDetail({ row, runs, onOpen }: { row: ImportRunRow; runs: readonly ImportRunRow[]; onOpen: (runId: string) => void }) {
  const t = useT();
  const tab = useTab();
  const detail = useQuery(closeImportRunQuery(tab, row.id));
  if (!detail.data) {
    return detail.isError ? <Problem error={detail.error} onRetry={() => void detail.refetch()} /> : <p className="g-ci__muted">{t("common.loading")}</p>;
  }
  const { run, summary, summaryStatus, progress } = detail.data;
  if (run.status === "running") return <RunningDetail run={run} progress={progress} readAt={detail.dataUpdatedAt} />;
  if (summaryStatus === "ready" && summary) return <CloseImportReport run={run} summary={summary} />;
  if (run.status === "completed") return <Reason tone="warning">{t("closeImport.unreadable")}</Reason>;
  // La successiva, nell'elenco dalla più recente, è quella subito sopra.
  const index = runs.findIndex((candidate) => candidate.id === run.id);
  const newer = index > 0 ? runs[index - 1] : undefined;
  return <StoppedDetail run={run} newer={newer} onOpen={onOpen} />;
}

/** Le fasi di un'esecuzione: «Scrive in Gioia365» soltanto nell'importazione vera. */
function phasesOf(run: ImportRunRow): Array<ImportRunRow["phase"]> {
  return run.mode === "apply" ? ["extract", "plan", "apply", "done"] : ["extract", "plan", "done"];
}

function DetailTitle({ id, sub }: { id: string; sub: string }) {
  const t = useT();
  return (
    <div className="g-ci__set-title">
      <h2 id={id}>{t("closeImport.summary.title")}</h2>
      <p className="g-ci__muted">{sub}</p>
    </div>
  );
}

function RunningDetail({ run, progress, readAt }: { run: ImportRunRow; progress: Record<string, { fetched: number }> | null; readAt: number }) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const titleId = useId();
  const timeZone = access.context.settings.timeZone;
  const when = runTitle(t, timeZone, run);
  const phases = phasesOf(run);
  const at = phases.indexOf(run.phase);
  const leads = progress?.lead?.fetched;
  const activities = progress?.activity?.fetched;
  // Il battito si confronta con l'ora in cui la pagina l'ha letto: una pagina che non riesce più a leggere non lo invecchia.
  const silent = readAt - Date.parse(run.heartbeatAt) > SILENT_AFTER_MS;
  const heartbeat = whenParts(timeZone, run.heartbeatAt).full;

  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <DetailTitle id={titleId} sub={t(run.mode === "dry_run" ? "closeImport.running.subDryRun" : "closeImport.running.subApply", { when })} />
      <ol className="g-ci__phases" aria-label={t("closeImport.phases.label")}>
        {phases.map((phase, index) => {
          const state = index < at ? "done" : index === at ? "now" : "todo";
          return (
            <li key={phase} data-state={state}>
              <Icon name={state === "done" ? "check" : state === "now" ? "clock" : "dot"} />
              <span>
                {t(`closeImport.phases.${phase}`)}
                <small>{t(state === "done" ? "closeImport.phases.stateDone" : state === "now" ? "closeImport.phases.stateNow" : "closeImport.phases.stateTodo")}</small>
              </span>
            </li>
          );
        })}
      </ol>
      {/* I conteggi cambiano a ogni rilettura (5 secondi) e non si annunciano: si annuncia soltanto la fase, quando cambia. */}
      <p className="g-visually-hidden" role="status">
        {t("closeImport.running.phaseNow", { phase: t(`closeImport.phases.${run.phase}`) })}
      </p>
      <div className="g-ci__progress">
        {leads === undefined ? <p>{t("closeImport.running.starting")}</p> : <p>{t("closeImport.running.leadsRead", { count: formatCount(locale, leads) })}</p>}
        {activities === undefined ? null : <p>{t("closeImport.running.activitiesRead", { count: formatCount(locale, activities) })}</p>}
      </div>
      {silent ? (
        <Reason tone="warning">{t("closeImport.running.silent", { time: heartbeat })}</Reason>
      ) : (
        <p className="g-ci__muted">{t("closeImport.running.heartbeat", { time: heartbeat })}</p>
      )}
      <p className="g-ci__muted">{t(run.mode === "dry_run" ? "closeImport.running.reportLaterDryRun" : "closeImport.running.reportLaterApply")}</p>
    </section>
  );
}

/** Perché un'esecuzione si è fermata, dal solo codice dell'errore (mai il messaggio tecnico). */
function StopReason({ code }: { code: string | null }) {
  const t = useT();
  if (code === null) return null;
  if (code === "close_auth_failed") return <p>{t("closeImport.stopped.reasonAuth")}</p>;
  if (code === "abandoned") return <p>{t("closeImport.stopped.reasonAbandoned")}</p>;
  return <p className="g-ci__muted">{t("closeImport.stopped.reasonCode", { code })}</p>;
}

function StoppedDetail({ run, newer, onOpen }: { run: ImportRunRow; newer: ImportRunRow | undefined; onOpen: (runId: string) => void }) {
  const t = useT();
  const access = useAccess();
  const titleId = useId();
  const timeZone = access.context.settings.timeZone;
  const duration = formatDuration(t, runDuration(run, Date.parse(run.heartbeatAt)));
  const phase = t(`closeImport.phases.${run.phase}`);
  const consequence =
    run.mode === "dry_run"
      ? t("closeImport.stopped.dryRun")
      : run.phase === "extract" || run.phase === "plan"
        ? t("closeImport.stopped.applyBeforeWriting")
        : t("closeImport.stopped.applyWhileWriting");
  return (
    <section className="g-ci__set" aria-labelledby={titleId}>
      <DetailTitle id={titleId} sub={runTitle(t, timeZone, run)} />
      <Reason tone="warning">{t(run.status === "aborted" ? "closeImport.stopped.aborted" : "closeImport.stopped.failed", { duration, phase })}</Reason>
      <StopReason code={run.errorCode ?? null} />
      <p>{consequence}</p>
      {newer ? (
        <p>
          <Button onClick={() => onOpen(newer.id)}>{t("closeImport.stopped.newer", whenParts(timeZone, newer.startedAt))}</Button>
        </p>
      ) : null}
    </section>
  );
}
